<script setup lang="ts">
import DatabaseDeleteBlockedDialog from '@/views/building_blocks/components/DatabaseDeleteBlockedDialog.vue'
import DatabaseSwitchValidationSummaryDialog from '@/components/calculation-form/environmental-database/DatabaseSwitchValidationSummaryDialog.vue'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { useEnvironmentDatabaseTab } from '@/components/calculation-form/environmental-database/composable/useEnvironmentDatabaseTab'

const props = withDefaults(defineProps<CalculationFormMixinProps>(), {
  tab: 'ENVIRONMENTAL_DATABASE',
  disabled: false,
  readonly: false
})

const {
  databaseRows,
  databaseOptions,
  selectedDatabase,
  onSelectedDatabaseChange,
  operationLoadingVisible,
  operationLoadingMessage,
  showSwitchValidationSummaryDialog,
  affectedBuildingBlocks,
  switchBaseDatabaseName,
  switchTargetDatabaseName,
  fileInput,
  importLoadingVisible,
  importLoadingMessage,
  handleImportEnvDb,
  onEnvDbFileSelected,
  canDelete,
  onDeleteDatabase,
  showDeleteBlockedDialog,
  deleteBlockedDatabaseName,
  deleteBlockedCalculationNames,
  deleteBlockedDialogTitle,
  deleteBlockedDialogDescription
} = useEnvironmentDatabaseTab(props)
</script>

<template>
  <v-container fluid class="pa-6">
    <v-overlay
      :model-value="operationLoadingVisible"
      class="d-flex align-center justify-center"
      persistent
    >
      <v-card class="operation-loading-card text-center py-7 px-8">
        <div class="d-flex justify-center mb-4">
          <v-progress-circular indeterminate color="primary" size="56" width="5" />
        </div>
        <div class="operation-loading-text">{{ operationLoadingMessage }}</div>
      </v-card>
    </v-overlay>

    <v-overlay
      :model-value="importLoadingVisible"
      class="d-flex align-center justify-center"
      persistent
    >
      <v-card class="operation-loading-card text-center py-7 px-8">
        <div class="d-flex justify-center mb-4">
          <v-progress-circular indeterminate color="primary" size="56" width="5" />
        </div>
        <div class="operation-loading-text">{{ importLoadingMessage }}</div>
      </v-card>
    </v-overlay>

    <v-row class="mb-4">
      <v-col cols="12">
        <h1 class="text-h4">Environmental Database</h1>
      </v-col>
      <v-col cols="12" class="pt-0">
        <div class="text-body-2 text-medium-emphasis">
          Review all environmental databases in this project. A green icon marks databases that are
          selected or used by one or more calculations.
        </div>
      </v-col>
    </v-row>

    <v-row class="mb-4 align-center ga-2">
      <v-col cols="12" md="6" lg="5">
        <v-select
          v-model="selectedDatabase"
          :items="databaseOptions"
          item-title="label"
          item-value="value"
          label="Selected database for this calculation"
          variant="outlined"
          density="comfortable"
          hide-details
          :disabled="readonly"
          @update:model-value="onSelectedDatabaseChange"
        />
      </v-col>
      <v-col cols="12" md="auto" class="d-flex align-center">
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls"
          style="display: none"
          @change="onEnvDbFileSelected"
        />
        <v-btn prepend-icon="mdi-upload" :disabled="readonly" @click="handleImportEnvDb">
          Import environmental database
        </v-btn>
      </v-col>
      <v-col cols="12" class="pt-0">
        <div class="text-body-2 text-medium-emphasis">
          This is the active environmental database for the current calculation. Use this tab to
          import project databases and manage which one Building Blocks uses for defaults and
          product search.
        </div>
      </v-col>
    </v-row>

    <v-row no-gutters class="px-3 mb-2">
      <v-col cols="6">
        <span class="text-body-2 font-weight-medium">Database</span>
      </v-col>
      <v-col cols="2">
        <span class="text-body-2 font-weight-medium">Weighting Set</span>
      </v-col>
      <v-col cols="4" class="text-right">
        <span class="text-body-2 font-weight-medium">Status</span>
      </v-col>
    </v-row>

    <div class="d-flex flex-column ga-3">
      <v-sheet v-for="row in databaseRows" :key="row.database.id" class="database-row pa-3">
        <v-row no-gutters align="center">
          <v-col cols="6" class="text-body-2">{{ row.database.name }}</v-col>
          <v-col cols="2" class="text-body-2 text-medium-emphasis">
            {{ row.database.weighting_set ?? '—' }}
          </v-col>
          <v-col cols="4" class="d-flex align-center justify-end ga-2">
            <v-tooltip v-if="row.status.tooltip" :text="row.status.tooltip">
              <template #activator="{ props: tooltipProps }">
                <v-chip
                  v-bind="tooltipProps"
                  size="small"
                  :color="row.status.color"
                  variant="tonal"
                  :prepend-icon="row.status.icon"
                >
                  {{ row.status.label }}
                </v-chip>
              </template>
            </v-tooltip>
            <v-chip v-else size="small" :color="row.status.color" variant="tonal">
              {{ row.status.label }}
            </v-chip>
            <v-btn
              icon="mdi-delete"
              variant="text"
              size="small"
              :color="row.database.in_use ? undefined : 'error'"
              :disabled="!canDelete(row.database)"
              @click="onDeleteDatabase(row.database)"
            />
          </v-col>
        </v-row>
      </v-sheet>
    </div>

    <DatabaseDeleteBlockedDialog
      v-model="showDeleteBlockedDialog"
      :title="deleteBlockedDialogTitle"
      :description="deleteBlockedDialogDescription"
      :database-name="deleteBlockedDatabaseName"
      :calculation-names="deleteBlockedCalculationNames"
    />

    <DatabaseSwitchValidationSummaryDialog
      v-model="showSwitchValidationSummaryDialog"
      :affected-blocks="affectedBuildingBlocks"
      :base-database-name="switchBaseDatabaseName"
      :target-database-name="switchTargetDatabaseName"
    />
  </v-container>
</template>

<style scoped>
.database-row {
  border: 1px solid #c0c0c0;
  border-radius: 10px;
  background-color: rgb(var(--v-theme-surface));
}

.operation-loading-card {
  width: 420px;
  max-width: calc(100vw - 48px);
}

.operation-loading-text {
  font-size: 0.95rem;
  font-weight: 400;
  line-height: 1.45;
}
</style>
